import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Shield, FileText, Mail, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Sezione "Privacy & Dati" nel portale utente — informativa, non auto-service.
 *
 * Rationale: i dati personali raccolti sono strettamente necessari alla gestione
 * del contratto di affitto (anagrafica, IBAN, documenti). Per obbligo di legge
 * (art. 2220 c.c.) devono essere conservati per 10 anni dal termine del contratto.
 * L'esercizio dei diritti GDPR (accesso, rettifica, cancellazione) viene gestito
 * manualmente via email per evitare interruzioni operative del contratto.
 */
const PrivacyInfoSection = () => {
  return (
    <div className="space-y-4" data-testid="gdpr-section">
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            La tua Privacy e i tuoi dati
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-700">
          {/* Cosa conserviamo */}
          <div className="bg-white border border-slate-200 rounded p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">Quali dati conserviamo</p>
                <p className="text-sm text-slate-600">
                  Trattiamo esclusivamente i dati <strong>strettamente necessari</strong> alla
                  gestione del tuo contratto di locazione: dati anagrafici, contatti, codice
                  fiscale, IBAN, documenti contrattuali e relativi pagamenti.
                  Nessun dato viene utilizzato per profilazione, marketing o ceduto a terze parti.
                </p>
              </div>
            </div>
          </div>

          {/* Conservazione obbligatoria */}
          <div className="bg-white border border-amber-200 rounded p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">Perché li conserviamo per 10 anni</p>
                <p className="text-sm text-slate-600">
                  La normativa italiana (art. 2220 Codice Civile) ci impone di conservare i
                  documenti contabili e fiscali (contratti, fatture, ricevute) per{' '}
                  <strong>10 anni</strong> dal termine del contratto.
                  Senza questi dati non potremmo gestire correttamente il rapporto contrattuale,
                  emettere fatture o rispondere a eventuali controlli dell'Agenzia delle Entrate.
                </p>
              </div>
            </div>
          </div>

          {/* Esercizio diritti */}
          <div className="bg-white border border-emerald-200 rounded p-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">
                  Esercita i tuoi diritti GDPR
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  Hai diritto di accedere, rettificare, limitare il trattamento o richiedere
                  la cancellazione dei tuoi dati (artt. 15-22 Reg. UE 2016/679). Per esercitare
                  questi diritti scrivi a:
                </p>
                <a
                  href="mailto:info@semplificaffitto.it?subject=Richiesta%20GDPR%20%E2%80%94%20Esercizio%20diritti%20Art.%2015-22"
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded transition-colors"
                  data-testid="gdpr-email-link"
                >
                  <Mail className="w-4 h-4" /> info@semplificaffitto.it
                </a>
                <p className="text-xs text-slate-500 mt-2">
                  Ti risponderemo entro <strong>30 giorni</strong>. Nota: per i dati soggetti
                  ad obbligo di conservazione (10 anni) procederemo, dove possibile, con
                  l'anonimizzazione mantenendo solo le informazioni necessarie agli adempimenti di legge.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 pt-2 border-t">
            Per il dettaglio completo delle finalità di trattamento, tempi di conservazione e
            tutti i tuoi diritti consulta l'{' '}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
              data-testid="gdpr-privacy-link"
            >
              Informativa Privacy completa
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyInfoSection;
